const PROD_SOCKET2 = 'https://socket2.zionyx.ch'
const PROD_SOCKET2_WSS = 'wss://socket2.zionyx.ch'
const PROD_LICENSES = 'https://licenses.zionyx.ch'
const PROD_WEB = 'https://www.zionyx.ch'

/** True in WKWebView Capacitor (iOS/Android nativo) — non in Safari PWA su zionyx.ch. */
export function isCapacitorNative(): boolean {
  try {
    if (typeof window === 'undefined') return false
    const Cap = (
      window as unknown as {
        Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string }
      }
    ).Capacitor
    if (typeof Cap?.isNativePlatform === 'function' && Cap.isNativePlatform()) return true
    const p = Cap?.getPlatform?.()
    if (p === 'ios' || p === 'android') return true
    const { protocol, hostname } = window.location
    // Capacitor con hostname www.zionyx.ch + scheme https resta nativo
    if (protocol === 'capacitor:' || protocol === 'ionic:') return true
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '' ||
      hostname === '0.0.0.0'
    )
  } catch {
    return false
  }
}

function isLocalOrNativeHost(): boolean {
  try {
    if (typeof window === 'undefined') return true
    if (isCapacitorNative()) return true
    const h = window.location.hostname
    return h === 'localhost' || h === '127.0.0.1' || h === '' || h === '0.0.0.0'
  } catch {
    return true
  }
}

/**
 * Base API socket2 — mai localhost su device reali.
 * Non lancia mai eccezioni.
 */
export function getSocket2Base(): string {
  try {
    if (isLocalOrNativeHost()) return PROD_SOCKET2
    if (import.meta.env.DEV) return '/socket2-proxy'
    if (typeof window !== 'undefined' && window.location.hostname.endsWith('zionyx.ch')) {
      return '/socket2-proxy'
    }
    return PROD_SOCKET2
  } catch {
    return PROD_SOCKET2
  }
}

/**
 * URL assoluto per fetch/EventSource.
 * Su Capacitor/iOS: SEMPRE https://socket2.zionyx.ch.
 * Mai throw — in caso di errore ripiega su URL produzione sicuro.
 */
export function resolveSocket2Url(path: string): string {
  try {
    const rawPath = path == null ? '' : String(path)
    const p = rawPath.startsWith('/') ? rawPath : `/${rawPath}`

    if (isCapacitorNative() || isLocalOrNativeHost()) {
      return `${PROD_SOCKET2}${p}`
    }

    const rawBase = getSocket2Base().replace(/\/$/, '')
    let base = rawBase
    if (!base.startsWith('http://') && !base.startsWith('https://')) {
      base =
        typeof window !== 'undefined' && window.location?.origin
          ? `${window.location.origin}${base.startsWith('/') ? base : `/${base}`}`
          : PROD_SOCKET2
    }

    try {
      const u = new URL(base)
      if (u.hostname === 'localhost' || u.hostname === '127.0.0.1') {
        base = PROD_SOCKET2
      }
    } catch {
      base = PROD_SOCKET2
    }

    return `${base}${p}`
  } catch (err) {
    console.warn('[ZION] resolveSocket2Url safe-fallback', err)
    try {
      const p = String(path ?? '').startsWith('/') ? String(path) : `/${String(path ?? '')}`
      return `${PROD_SOCKET2}${p}`
    } catch {
      return PROD_SOCKET2
    }
  }
}

/**
 * URL WebSocket esplicito.
 * Capacitor iOS/Android: SEMPRE wss://socket2.zionyx.ch/... (mai proxy, mai localhost).
 * Nessun controllo Origin — WebKit non consente di impostare Origin sul handshake WS.
 */
export function resolveSocket2WsUrl(path: string): string {
  try {
    const rawPath = path == null ? '' : String(path)
    // Se arriva già un URL assoluto, normalizza host/protocollo
    if (/^wss?:\/\//i.test(rawPath) || /^https?:\/\//i.test(rawPath)) {
      try {
        const u = new URL(rawPath.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:'))
        if (isCapacitorNative() || isLocalOrNativeHost()) {
          return `${PROD_SOCKET2_WSS}${u.pathname}${u.search}${u.hash}`
        }
        if (u.hostname === 'localhost' || u.hostname === '127.0.0.1') {
          return `${PROD_SOCKET2_WSS}${u.pathname}${u.search}${u.hash}`
        }
        u.protocol = 'wss:'
        return u.toString()
      } catch {
        /* fall through */
      }
    }
    const p = rawPath.startsWith('/') ? rawPath : `/${rawPath}`
    if (isCapacitorNative() || isLocalOrNativeHost()) {
      return `${PROD_SOCKET2_WSS}${p}`
    }
    const httpsUrl = resolveSocket2Url(p)
    if (httpsUrl.startsWith('https://')) return `wss://${httpsUrl.slice('https://'.length)}`
    if (httpsUrl.startsWith('http://')) return `ws://${httpsUrl.slice('http://'.length)}`
    if (httpsUrl.startsWith('wss://') || httpsUrl.startsWith('ws://')) return httpsUrl
    return `${PROD_SOCKET2_WSS}${p}`
  } catch {
    return PROD_SOCKET2_WSS
  }
}

/** Host WSS produzione (Capacitor). */
export function getSocket2WssBase(): string {
  return PROD_SOCKET2_WSS
}

/**
 * Fetch verso socket2 che non propaga crash al rendering:
 * timeout / rete / LOAD FAILED → null (caller gestisce offline).
 * Su Capacitor usa URL assoluto (CapacitorHttp bypassa CORS).
 */
export async function safeSocket2Fetch(
  path: string,
  init: RequestInit = {},
  timeoutMs = 8_000,
): Promise<Response | null> {
  try {
    const url = resolveSocket2Url(path)
    const ctrl = new AbortController()
    const timer = window.setTimeout(() => ctrl.abort(), timeoutMs)
    const userSignal = init.signal
    if (userSignal) {
      if (userSignal.aborted) ctrl.abort()
      else userSignal.addEventListener('abort', () => ctrl.abort(), { once: true })
    }
    try {
      // Non impostare Origin: il browser/CapacitorHttp lo gestiscono.
      // Su native CapacitorHttp evita il blocco CORS capacitor://.
      const headers = new Headers(init.headers)
      headers.delete('Origin')
      headers.delete('origin')
      return await fetch(url, { ...init, headers, signal: ctrl.signal, mode: 'cors', credentials: 'omit' })
    } finally {
      window.clearTimeout(timer)
    }
  } catch (err) {
    console.warn('[ZION] safeSocket2Fetch offline / timeout', err)
    return null
  }
}

/** Piattaforma Capacitor (`web` | `android` | `ios` | …). */
export function getCapacitorPlatform(): string {
  try {
    const Cap = (
      window as unknown as { Capacitor?: { getPlatform?: () => string } }
    ).Capacitor
    return String(Cap?.getPlatform?.() ?? '').trim().toLowerCase()
  } catch {
    return ''
  }
}

/** URL assoluto o relativo per asset/API web su zionyx.ch. */
export function resolveWebAppUrl(path: string): string {
  try {
    const raw = path == null ? '' : String(path)
    const p = raw.startsWith('/') ? raw : `/${raw}`
    if (isCapacitorNative() || isLocalOrNativeHost()) {
      return `${PROD_WEB}${p}`
    }
    if (typeof window !== 'undefined' && window.location.hostname.endsWith('zionyx.ch')) {
      return p
    }
    if (import.meta.env.DEV) {
      return p
    }
    return `${PROD_WEB}${p}`
  } catch {
    return `${PROD_WEB}/app/`
  }
}

/** Base URL API — relativo all'host corrente (PWA). */
export const ZION_API_BASE = ''

/** @deprecated Preferire getSocket2Base() / resolveSocket2Url() a runtime. */
export const SOCKET2_BASE: string = PROD_SOCKET2

export const LICENSES_BASE =
  import.meta.env.DEV && !isLocalOrNativeHost() ? '/licenses-proxy' : PROD_LICENSES

export const ONYX_APP_HEADER = 'zion'

export const SOCKET2_DIRECT = PROD_SOCKET2

export const SOCKET2_WSS = PROD_SOCKET2_WSS

export const PUSH_API_BASE = isLocalOrNativeHost() ? PROD_WEB : ''

/**
 * Soft renew sessione dopo 401/403 — non blocca UI.
 * Delega a ZionSocketClient (mutex + reconnect background).
 */
export async function refreshSessionTokenTransparent(): Promise<string | null> {
  try {
    const { refreshSessionTokenTransparent: refresh } = await import('../socket2/ZionSocketClient.ts')
    return await refresh()
  } catch (e) {
    console.warn('[ZION] ZionServer.refreshSessionTokenTransparent', e)
    return null
  }
}
